import { useState } from 'react';

import PropTypes from 'prop-types';
import {
	BsSearch,
	BsGeoAlt,
	BsFileEarmarkText,
	BsBuilding,
	BsCalendarEvent,
	BsInfoCircle,
	BsTagsFill,
	BsExclamationTriangle,
} from 'react-icons/bs';

import { useContactLists } from '#contactListFeature/models/queries';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import { GERMAN_STATES, GERMAN_STATE_LABELS } from '#lib/constants/germanStates';

import { useB2bHeroLeadSearchQueries } from '../../../models/prospectQueries/queries';
import {
	LEGAL_FORM_OPTIONS,
	COUNTRY_OPTIONS,
	EVENT_TYPE_OPTIONS,
	WEBSITE_FILTER_OPTIONS,
} from '../searchFilters/components/filterGroups/shared';
import { WzCodePickerSection } from '../WzCodePickerModal/WzCodePickerSection';

import { CityAutocompleteInput } from './CityAutocompleteInput';
import { CollapseCard } from './CollapseCard';
import { TextInput, Select, DateInput, RangeInput, SegmentedToggle } from './criteriaFormAtoms';
import { IndustryTermsInput } from './IndustryTermsInput';
import { MultiSelect } from './MultiSelect';
import { RadiusMap } from './RadiusMap';
import { TargetingCriteriaCards } from './TargetingCriteriaCards';

const STATE_OPTIONS = GERMAN_STATES.map((s) => ({ value: s.code, label: s.label }));

const LOCKED_TYPE_REASON = { hr: 'Nicht für Handelsregister-Suchen', maps: 'Nicht für Karten-Suchen' };

const splitTerms = (s) =>
	(s || '')
		.split(',')
		.map((x) => x.trim())
		.filter(Boolean);

/**
 * One-line summaries shown on collapsed cards.
 * @param {object} filters - UI filter state (DEFAULT_FILTERS shape)
 * @param {boolean} isHR
 * @param {boolean} isGermany
 * @returns {Record<string, string>}
 */
const buildSummaries = (filters, isHR, isGermany) => {
	const hasWzCodes = Array.isArray(filters.industryCodes) && filters.industryCodes.length > 0;
	const isPlzMode = filters.locationMode === 'plz';
	const hasPlzRange = Boolean(filters.plzFrom && filters.plzTo);
	const hasPlzList = Boolean(filters.plzList?.trim());
	const stateCount = (filters.states || []).length;
	const terms = splitTerms(filters.industrySearchTerms);
	const lf = filters.legalForm || [];
	const sBranche = terms.length
		? terms.slice(0, 2).join(', ') + (terms.length > 2 ? ` +${terms.length - 2}` : '')
		: hasWzCodes
			? `${filters.industryCodes.length} WZ-Codes`
			: 'Keine';
	const keywordTerms = splitTerms(filters.searchKeywords);
	const excludeTerms = splitTerms(filters.excludeKeywords);
	const searchbegriffeBits = [];
	if (isHR && keywordTerms.length) searchbegriffeBits.push(`${keywordTerms.length} Stichw.`);
	if (!isHR && filters.onlyNameMatches) searchbegriffeBits.push('Nur Namens-Treffer');
	if (excludeTerms.length) searchbegriffeBits.push(`${excludeTerms.length} Ausschl.`);
	const sSuchbegriffe = searchbegriffeBits.length ? searchbegriffeBits.join(' · ') : 'Nicht gesetzt';
	let sStandort;
	if (isPlzMode) {
		const plzBits = [];
		if (hasPlzRange) plzBits.push(`${filters.plzFrom}–${filters.plzTo}`);
		if (hasPlzList) plzBits.push(filters.plzList.trim());
		sStandort = plzBits.length ? `PLZ ${plzBits.join(', ')}` : 'PLZ wählen';
	} else if (filters.locationMode === 'state') {
		if (stateCount === 0) sStandort = 'Bundesland wählen';
		else sStandort = stateCount === 1 ? GERMAN_STATE_LABELS[filters.states[0]] : `${stateCount} Bundesländer`;
	} else if (filters.location) {
		sStandort = `${filters.location}${filters.radius ? ` · ${filters.radius} km` : ''}`;
	} else {
		sStandort = isHR && isGermany ? 'Deutschlandweit' : `Ganz ${filters.country || 'Deutschland'}`;
	}
	const exclQueryCount = (filters.excludeProspectQueryIds || []).length;
	const unternehmenBits = [];
	if (lf.length) unternehmenBits.push(lf.slice(0, 2).join(', ') + (lf.length > 2 ? ` +${lf.length - 2}` : ''));
	if (exclQueryCount) unternehmenBits.push(`${exclQueryCount} Suchen ausgeschl.`);
	const exclListCount = (filters.excludeContactListIds || []).length;
	if (exclListCount) unternehmenBits.push(`${exclListCount} Listen ausgeschl.`);
	const sUnternehmen = unternehmenBits.length ? unternehmenBits.join(' · ') : 'Alle Rechtsformen';
	const finBits = [];
	if (filters.revenueFrom || filters.revenueTo)
		finBits.push(`${filters.revenueFrom || '0'}–${filters.revenueTo || '∞'} Mio. Umsatz`);
	if (filters.balanceTotalFrom || filters.balanceTotalTo)
		finBits.push(`${filters.balanceTotalFrom || '0'}–${filters.balanceTotalTo || '∞'} Mio. Bilanz`);
	if (filters.earningsFrom || filters.earningsTo)
		finBits.push(`${filters.earningsFrom || '0'}–${filters.earningsTo || '∞'} Mio. Gewinn`);
	if (filters.employeesFrom || filters.employeesTo)
		finBits.push(`${filters.employeesFrom || '0'}–${filters.employeesTo || '∞'} MA`);
	const sFinanzen = finBits.length ? finBits.join(' · ') : 'Nicht gesetzt';
	const eventTypeLabel = filters.eventType
		? EVENT_TYPE_OPTIONS.find((o) => o.value === filters.eventType)?.label || filters.eventType
		: null;
	const sEreignisse = eventTypeLabel
		? `${eventTypeLabel}${filters.eventDateFrom || filters.eventDateTo ? ' · Zeitraum' : ''}`
		: 'Nicht gesetzt';
	const revBits = [];
	if (filters.ratingFrom || filters.ratingTo)
		revBits.push(`${filters.ratingFrom || '1'}–${filters.ratingTo || '5'} ★`);
	if (filters.minReviewCount) revBits.push(`≥ ${filters.minReviewCount} Rez.`);
	const sBewertungen = revBits.length ? revBits.join(' · ') : 'Nicht gesetzt';

	return {
		branche: sBranche,
		suchbegriffe: sSuchbegriffe,
		standort: sStandort,
		unternehmen: sUnternehmen,
		finanzen: sFinanzen,
		ereignisse: sEreignisse,
		bewertungen: sBewertungen,
	};
};

/**
 * The filter cards of the search criteria grid, shared by the new-search and the extend-search modal.
 * Maps/HR exclusivity is enforced by disabling the other type's cards: derived from the active filters
 * while the type is open, or fixed by `lockedSearchType` (an extension keeps its query's type).
 * Children render as the first grid cells.
 */
export const SearchCriteriaCards = ({
	filters,
	searchType,
	isGermany,
	hasActiveHRFilters,
	hasActiveMapsFilters,
	onFilterChange,
	lockedSearchType = null,
	currentQueryId = null,
	children,
}) => {
	// Shared queries are excluded: the backend ownership check only accepts org-owned queries
	const existingQueriesResult = useB2bHeroLeadSearchQueries();
	const excludableQueryOptions = (existingQueriesResult.data?.data || [])
		.filter((q) => !q.isShared && q.id !== currentQueryId)
		.map((q) => ({ value: q.id, label: q.queryName }));

	const contactListsResult = useContactLists();
	const excludableListOptions = (contactListsResult.data || []).map((l) => ({ value: l.id, label: l.name }));

	const [groups, setGroups] = useState({
		branche: true,
		suchbegriffe: false,
		standort: true,
		unternehmen: false,
		finanzen: false,
		ereignisse: false,
		bewertungen: false,
	});
	const toggleGroup = (k) => setGroups((p) => ({ ...p, [k]: !p[k] }));

	const isHR = searchType === 'hr';
	const isHRDisabled = lockedSearchType ? lockedSearchType !== 'hr' : !isGermany || hasActiveMapsFilters;
	const isMapsDisabled = lockedSearchType ? lockedSearchType !== 'maps' : hasActiveHRFilters;
	const hrReason = lockedSearchType
		? LOCKED_TYPE_REASON[lockedSearchType]
		: !isGermany
			? 'Nur in Deutschland'
			: 'Bewertungs-Filter aktiv';
	const mapsReason = lockedSearchType ? LOCKED_TYPE_REASON[lockedSearchType] : 'Handelsregister-Filter aktiv';

	const isPlzMode = isGermany && filters.locationMode === 'plz';
	const isStateMode = isGermany && filters.locationMode === 'state';
	const exclListCount = (filters.excludeContactListIds || []).length;
	const summaries = buildSummaries(filters, isHR, isGermany);

	return (
		<>
			{children}
			<CollapseCard
				icon={BsSearch}
				title="Branche"
				summary={summaries.branche}
				open={groups.branche}
				onToggle={() => toggleGroup('branche')}
			>
				<IndustryTermsInput
					value={filters.industrySearchTerms}
					onChange={(v) => onFilterChange('industrySearchTerms', v)}
				/>
				<Select
					id="criteria-website-filter"
					label="Website"
					options={WEBSITE_FILTER_OPTIONS}
					value={filters.websiteFilter}
					onChange={(value) => onFilterChange('websiteFilter', value || 'all')}
				/>
				{/* WZ-2025 manual picker (HR only) */}
				<WzCodePickerSection filters={filters} onFilterChange={onFilterChange} disabled={isHRDisabled} />
			</CollapseCard>

			<CollapseCard
				icon={BsGeoAlt}
				title="Standort"
				span2
				summary={summaries.standort}
				open={groups.standort}
				onToggle={() => toggleGroup('standort')}
			>
				{isGermany && (
					<SegmentedToggle
						options={[
							{ value: 'place', label: 'Ort + Umkreis' },
							{ value: 'state', label: 'Bundesland' },
							{ value: 'plz', label: 'PLZ' },
						]}
						value={filters.locationMode || 'place'}
						onChange={(v) => onFilterChange('locationMode', v)}
					/>
				)}
				{isPlzMode && (
					<>
						<div className="grid grid-cols-2 gap-3">
							<TextInput
								id="criteria-plz-from"
								label="PLZ-Bereich von (optional)"
								placeholder="z.B. 60000"
								value={filters.plzFrom}
								onChange={(v) => onFilterChange('plzFrom', v.replace(/\D/g, '').slice(0, 5))}
							/>
							<TextInput
								id="criteria-plz-to"
								label="PLZ-Bereich bis (optional)"
								placeholder="z.B. 61999"
								value={filters.plzTo}
								onChange={(v) => onFilterChange('plzTo', v.replace(/\D/g, '').slice(0, 5))}
							/>
						</div>
						<TextInput
							id="criteria-plz-list"
							label="Einzelne PLZ / Präfixe (optional)"
							placeholder="z.B. 38124, 38126, 39*"
							value={filters.plzList}
							onChange={(v) => onFilterChange('plzList', v)}
						/>
						<p className="text-[11px] text-gray-400">
							Bereich und/oder Liste kombinierbar: einzelne PLZ (38124), Bereiche (38100-38200) und
							Präfixe (38*), per Komma getrennt.
						</p>
					</>
				)}
				{isStateMode && (
					<div>
						<label className="block text-xs text-bgBase-800 mb-1">Bundesland</label>
						<MultiSelect
							options={STATE_OPTIONS}
							selected={filters.states || []}
							onChange={(v) => onFilterChange('states', v)}
							placeholder="Bundesland wählen"
						/>
					</div>
				)}
				{!isPlzMode && !isStateMode && (
					<>
						<div className="grid grid-cols-2 gap-3">
							<CityAutocompleteInput
								id="criteria-loc"
								label="Stadt oder PLZ"
								placeholder="Leer = ganzes Land"
								value={filters.location}
								onChange={(v) => onFilterChange('location', v)}
							/>
							<Select
								id="criteria-country"
								label="Land"
								options={COUNTRY_OPTIONS}
								value={filters.country || 'Deutschland'}
								onChange={(v) => onFilterChange('country', v)}
								disabled={lockedSearchType === 'hr'}
							/>
						</div>
						<RadiusMap
							location={filters.location}
							country={filters.country || 'Deutschland'}
							radius={filters.radius}
							onRadius={(v) => onFilterChange('radius', v)}
						/>
					</>
				)}
			</CollapseCard>

			{isHR && <TargetingCriteriaCards filters={filters} onFilterChange={onFilterChange} />}

			<CollapseCard
				icon={BsFileEarmarkText}
				title="Unternehmen"
				type="hr"
				span2
				summary={summaries.unternehmen}
				open={groups.unternehmen}
				onToggle={() => toggleGroup('unternehmen')}
				disabled={isHRDisabled}
				reason={hrReason}
			>
				<div>
					<label className="block text-xs text-bgBase-800 mb-1">Rechtsform</label>
					<MultiSelect
						options={LEGAL_FORM_OPTIONS}
						selected={filters.legalForm || []}
						onChange={(v) => onFilterChange('legalForm', v)}
						placeholder="Rechtsform auswählen..."
					/>
				</div>
				{excludableQueryOptions.length > 0 && (
					<div>
						<label className="block text-xs text-bgBase-800 mb-1">
							Firmen aus früheren Suchen ausschließen
						</label>
						<MultiSelect
							options={excludableQueryOptions}
							selected={filters.excludeProspectQueryIds || []}
							onChange={(v) => onFilterChange('excludeProspectQueryIds', v)}
							placeholder="Keine Suchen ausgeschlossen"
						/>
						<p className="text-[11px] text-bgBase-800 mt-1">
							Firmen, die bereits in den gewählten Suchen enthalten sind, werden übersprungen (max. 20
							Suchen).
						</p>
					</div>
				)}
				{excludableListOptions.length > 0 && (
					<div>
						<label className="block text-xs text-bgBase-800 mb-1">
							Firmen aus Kontaktlisten ausschließen
						</label>
						<MultiSelect
							options={excludableListOptions}
							selected={filters.excludeContactListIds || []}
							onChange={(v) => onFilterChange('excludeContactListIds', v)}
							placeholder="Keine Kontaktlisten ausgeschlossen"
						/>
						<p className="text-[11px] text-bgBase-800 mt-1">
							Firmen der gewählten Listen (inkl. der Firmen enthaltener Kontakte) werden übersprungen
							(max. 50 Listen).
						</p>
						{exclListCount > 0 && (
							<div className="mt-2 p-2 rounded-md bg-amber-50 text-[11px] text-amber-800 flex items-start gap-2">
								<BsExclamationTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
								<span>
									Erkannt werden nur Firmen, die aus einer Lead-Suche stammen. Importierte oder
									manuell angelegte CRM-Firmen können nicht ausgeschlossen werden.
								</span>
							</div>
						)}
					</div>
				)}
			</CollapseCard>

			<CollapseCard
				icon={BsBuilding}
				title="Finanzen & Mitarbeiter"
				type="hr"
				span2
				summary={summaries.finanzen}
				open={groups.finanzen}
				onToggle={() => toggleGroup('finanzen')}
				disabled={isHRDisabled}
				reason={hrReason}
			>
				<div className="p-2 rounded-md bg-gray-50 text-[11px] text-bgBase-800 flex items-start gap-2">
					<BsInfoCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
					<span>
						Wenn Sie Finanzfilter setzen, werden nur Unternehmen mit hinterlegten Finanzdaten angezeigt.
					</span>
				</div>
				<div className="grid grid-cols-2 gap-3">
					<div>
						<label className="block text-xs text-bgBase-800 mb-1">Umsatz (Mio. €)</label>
						<RangeInput
							fromValue={filters.revenueFrom}
							toValue={filters.revenueTo}
							onFrom={(v) => onFilterChange('revenueFrom', v)}
							onTo={(v) => onFilterChange('revenueTo', v)}
							unit="Mio. €"
							step={0.1}
							min={0}
						/>
					</div>
					<div>
						<label className="block text-xs text-bgBase-800 mb-1">Bilanzsumme (Mio. €)</label>
						<RangeInput
							fromValue={filters.balanceTotalFrom}
							toValue={filters.balanceTotalTo}
							onFrom={(v) => onFilterChange('balanceTotalFrom', v)}
							onTo={(v) => onFilterChange('balanceTotalTo', v)}
							unit="Mio. €"
							step={0.1}
							min={0}
						/>
					</div>
					<div>
						<label className="block text-xs text-bgBase-800 mb-1">Gewinn (Mio. €)</label>
						<RangeInput
							fromValue={filters.earningsFrom}
							toValue={filters.earningsTo}
							onFrom={(v) => onFilterChange('earningsFrom', v)}
							onTo={(v) => onFilterChange('earningsTo', v)}
							unit="Mio. €"
							step={0.1}
						/>
					</div>
					<div>
						<label className="block text-xs text-bgBase-800 mb-1">Mitarbeiteranzahl</label>
						<RangeInput
							fromValue={filters.employeesFrom}
							toValue={filters.employeesTo}
							onFrom={(v) => onFilterChange('employeesFrom', v)}
							onTo={(v) => onFilterChange('employeesTo', v)}
							step={1}
							min={0}
						/>
					</div>
				</div>
				<Checkbox
					id="criteria-incl-est"
					checked={filters.includeEstimated !== false}
					onChange={(c) => onFilterChange('includeEstimated', c)}
					label="Geschätzte Werte einbeziehen"
				/>
			</CollapseCard>

			<CollapseCard
				icon={BsCalendarEvent}
				title="Ereignisse"
				type="hr"
				span2
				summary={summaries.ereignisse}
				open={groups.ereignisse}
				onToggle={() => toggleGroup('ereignisse')}
				disabled={isHRDisabled}
				reason={hrReason}
			>
				<div className="grid grid-cols-3 gap-3">
					<Select
						id="criteria-event-type"
						label="Ereignistyp"
						options={EVENT_TYPE_OPTIONS}
						value={filters.eventType || ''}
						onChange={(v) => onFilterChange('eventType', v || '')}
						placeholderOption="Alle Ereignisse"
					/>
					<DateInput
						id="criteria-event-from"
						label="Ereignis ab"
						value={filters.eventDateFrom}
						onChange={(v) => onFilterChange('eventDateFrom', v)}
					/>
					<DateInput
						id="criteria-event-to"
						label="Ereignis bis"
						value={filters.eventDateTo}
						onChange={(v) => onFilterChange('eventDateTo', v)}
					/>
				</div>
			</CollapseCard>

			<CollapseCard
				icon={BsTagsFill}
				title="Such- & Ausschlussbegriffe"
				span2
				summary={summaries.suchbegriffe}
				open={groups.suchbegriffe}
				onToggle={() => toggleGroup('suchbegriffe')}
			>
				{/* HR-only keyword field: searches name, purpose and categories.
					    Hidden for Maps searches where it has no effect. */}
				{isHR && (
					<div>
						<TextInput
							id="criteria-search-keywords"
							label="Stichwörter (kommagetrennt)"
							placeholder="z.B. Solar, Photovoltaik"
							value={filters.searchKeywords}
							onChange={(v) => onFilterChange('searchKeywords', v)}
						/>
						<p className="text-[10px] text-bgBase-800 mt-0.5">
							Sucht im Firmennamen, Geschäftszweck und Kategorien
						</p>
					</div>
				)}

				{/* Maps-only name match: delegates to Apify searchMatching 'only_includes'. */}
				{!isHR && (
					<div>
						<Checkbox
							id="criteria-only-name-matches"
							checked={Boolean(filters.onlyNameMatches)}
							onChange={(c) => onFilterChange('onlyNameMatches', c)}
							label="Nur Orte mit Suchbegriff im Namen"
						/>
						<p className="text-[10px] text-bgBase-800 mt-0.5">
							Für Markensuchen: nur Treffer, deren Name den Suchbegriff enthält
						</p>
					</div>
				)}

				<div>
					<TextInput
						id="criteria-exclude-keywords"
						label="Ausschlussbegriffe (kommagetrennt)"
						placeholder="z.B. Holding, Filiale"
						value={filters.excludeKeywords}
						onChange={(v) => onFilterChange('excludeKeywords', v)}
					/>
					<p className="text-[10px] text-bgBase-800 mt-0.5">
						Firmen mit diesen Begriffen im Namen werden ausgeschlossen
					</p>
				</div>
			</CollapseCard>

			<CollapseCard
				icon={BsInfoCircle}
				title="Bewertungen"
				type="maps"
				span2
				summary={summaries.bewertungen}
				open={groups.bewertungen}
				onToggle={() => toggleGroup('bewertungen')}
				disabled={isMapsDisabled}
				reason={mapsReason}
			>
				<div className="grid grid-cols-2 gap-3">
					<div>
						<label className="block text-xs text-bgBase-800 mb-1">Google Bewertung (Sterne)</label>
						<RangeInput
							fromValue={filters.ratingFrom}
							toValue={filters.ratingTo}
							onFrom={(v) => onFilterChange('ratingFrom', v)}
							onTo={(v) => onFilterChange('ratingTo', v)}
							fromPlaceholder="Min (1.0)"
							toPlaceholder="Max (5.0)"
							step={0.1}
							min={1}
						/>
					</div>
					<TextInput
						id="criteria-minrev"
						label="Min. Anzahl Bewertungen"
						placeholder="z.B. 10"
						value={filters.minReviewCount}
						onChange={(v) => onFilterChange('minReviewCount', v)}
					/>
				</div>
			</CollapseCard>
		</>
	);
};

SearchCriteriaCards.propTypes = {
	filters: PropTypes.object.isRequired,
	searchType: PropTypes.oneOf(['maps', 'hr']).isRequired,
	isGermany: PropTypes.bool.isRequired,
	hasActiveHRFilters: PropTypes.bool.isRequired,
	hasActiveMapsFilters: PropTypes.bool.isRequired,
	onFilterChange: PropTypes.func.isRequired,
	lockedSearchType: PropTypes.oneOf(['maps', 'hr']),
	currentQueryId: PropTypes.string,
	children: PropTypes.node,
};
